import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import axios from "axios";
import { 
  Calendar,
  ArrowRight,
  Newspaper
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://api.afrigulfmarket.com";

const Blog = () => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blog`);
      setPosts(response.data);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    }
    setLoading(false);
  };

  // Demo posts if none exist
  const demoPosts = [
    {
      id: '1',
      title: 'Cross-Border Trade: UAE to East Africa Opportunities',
      slug: 'cross-border-trade-uae-east-africa',
      content: 'Exploring the growing trade corridors between the Gulf region and East African markets...',
      cover_image_path: 'https://images.unsplash.com/photo-1649519764734-85758bda0302?w=800&q=80',
      created_at: '2024-01-15T00:00:00Z',
      published: true
    },
    {
      id: '2',
      title: 'Understanding Trade Finance: Letters of Credit Explained',
      slug: 'understanding-trade-finance-letters-of-credit',
      content: 'A comprehensive guide to Letters of Credit (LC) and how they secure international transactions...',
      cover_image_path: 'https://images.unsplash.com/photo-1583737077382-3f51318d6074?w=800&q=80',
      created_at: '2024-01-10T00:00:00Z',
      published: true
    },
    {
      id: '3',
      title: 'Shipping Routes: Optimizing Logistics from Dubai to Mombasa',
      slug: 'shipping-routes-dubai-mombasa',
      content: 'Best practices for shipping goods from UAE ports to Kenya\'s main gateway port...',
      cover_image_path: 'https://images.unsplash.com/photo-1726533765275-a69cfd7f9897?w=800&q=80',
      created_at: '2024-01-05T00:00:00Z',
      published: true
    }
  ];

  const displayPosts = posts.length > 0 ? posts : demoPosts;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="blog-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="container-main py-8">
          <span className="overline text-[#0D4F8B] mb-3 block">{t('blog.overline')}</span>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('blog.title')}
          </h1>
        </div>
      </div>

      <div className="container-main py-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="card-base overflow-hidden">
                <Skeleton className="h-48 w-full" />
                <div className="p-6 space-y-3">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </Card>
            ))}
          </div>
        ) : displayPosts.length === 0 ? (
          <div className="text-center py-16">
            <Newspaper className="w-16 h-16 text-slate-300 mx-auto mb-4" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No articles yet</h3>
            <p className="text-slate-500">Check back soon for trade insights and updates</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayPosts.map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`}>
                <Card className="card-base card-interactive hover-lift overflow-hidden h-full" data-testid={`blog-post-${post.slug}`}>
                  <div 
                    className="h-48 bg-cover bg-center"
                    style={{
                      backgroundImage: post.cover_image_path 
                        ? `url('${post.cover_image_path}')`
                        : 'linear-gradient(to br, #0D4F8B, #10B981)'
                    }}
                  />
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                      <Calendar className="w-4 h-4" aria-hidden="true" />
                      {new Date(post.created_at).toLocaleDateString()}
                    </div>
                    <h3 className="font-semibold text-lg text-slate-900 mb-3 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-slate-600 line-clamp-3 mb-4">
                      {post.content.substring(0, 150)}...
                    </p>
                    <span className="inline-flex items-center text-[#0D4F8B] font-medium text-sm group-hover:underline">
                      {t('blog.readMore')} <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;

